import { Box, Divider, IconButton, List, ListItem, Paper, Popover, Radio, TextField, Typography, useTheme } from "@material-ui/core";
import KeyboardArrowDownSharpIcon from "@material-ui/icons/KeyboardArrowDownSharp";
import Month from "apps/platform-client/client/src/components/date-range-picker/Month";
import { caseEventLogOptions, DateRangeLabelOptions, options } from "apps/platform-client/client/src/components/date-range-picker/utils";
import { Maybe } from "apps/platform-client/client/src/models/generated/graphql";
import { addMonths } from "date-fns";
import { Fragment, memo } from "react";
import { useTranslation } from "react-i18next";
import InputMask from "react-input-mask";
import { useStyles } from "./styles";
import DatePickerErrorMessage from "./ErrorMessage";
import { dateFormatDateRange, dateFormatMonthRange, dateMaskFormat, getOptionsToExclude, maskDates, maskMonths } from "./DatePicker";

export interface ICustomDatePickerProps {
	startDate?: Maybe<Date>;
	endDate?: Maybe<Date>;
	label?: string;
	required?: boolean;
	option?: Maybe<DateRangeLabelOptions>;
	contractStartDate?: Maybe<Date>;
	contractEndDate?: Maybe<Date>;
	enrollmentPeriods?: Maybe<Array<{ from: Date; to: Date }>>;
	selectMonths?: Maybe<boolean>;
	onChangeRange?: (startDate?: Maybe<Date>, endDate?: Maybe<Date>, option?: Maybe<DateRangeLabelOptions>) => void;
	labelStyles?: React.CSSProperties;
	isCaseEventLogFilter?: boolean;
	isAllTimeOptionAvailable?: boolean;
	error?: string;
	labelBg?: string;
	checkDate?: boolean;
	handleClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
	handleClose: () => void;
	handleGoToNextMonth: () => void;
	handleGoToPreviousMonths: () => void;
	handleClickOption: (o: DateRangeLabelOptions) => void;
	setStartDate: () => void;
	setEndDate: () => void;
	handleSelectDate: (date: Date) => void;
	handleChangeStartDate: (event: React.ChangeEvent<HTMLInputElement>) => void;
	handleChangeEndDate: (event: React.ChangeEvent<HTMLInputElement>) => void;
	handleLeaveFocus: (property: "rawStartValue" | "rawEndValue") => void;
	clearFilter?: () => void;
	state: {
		anchorEl?: Maybe<Element>;
		startDate?: Maybe<Date>;
		endDate?: Maybe<Date>;
		option?: Maybe<DateRangeLabelOptions>;
		firstCalendarDate: Date;
		rawStartValue: string;
		rawEndValue: string;
	};
	startDateInvalid: any;
	endDateInvalid: any;
}

function DashboardDatePicker(props: ICustomDatePickerProps) {
	const { t } = useTranslation();
	const { palette, spacing } = useTheme();
	const dateFormat = dateMaskFormat(!!props.selectMonths, dateFormatMonthRange, dateFormatDateRange);
	const mask = dateMaskFormat(!!props.selectMonths, maskMonths, maskDates);
	const rangeOptions = props.isCaseEventLogFilter ? caseEventLogOptions : options;
	const optionsToExclude = getOptionsToExclude(props.contractStartDate || new Date());
	const filteredOptions = props.contractStartDate ? rangeOptions.filter((o) => !optionsToExclude.includes(o)) : rangeOptions;
	const enabledOptions = filteredOptions.filter(
		(o) => (o !== "sinceStart" || !!props.contractStartDate) && (o !== "allTime" || props.isAllTimeOptionAvailable)
	);
	const months = [props.state.firstCalendarDate, addMonths(props.state.firstCalendarDate, 1)];
	const isValidationError =
		props.error || (!!props.state.startDate && !!props.state.endDate && props.state.startDate > props.state.endDate);
	const classes = useStyles();

	const fieldValue = (option: Maybe<DateRangeLabelOptions> | undefined) => {
		if (!option) {
			return t("time-period");
		}
		if (option === "custom") {
			return `${props.state.rawStartValue} - ${props.state.rawEndValue}`;
		}
		return t(option);
	};

	return (
		<Paper
			variant="outlined"
			style={{
				position: "relative",
			}}
		>
			<Typography
				variant="caption"
				style={{
					paddingLeft: spacing(0.8),
					paddingRight: spacing(0.8),
					color: palette.primary.main,
					position: "absolute",
					left: "10px",
					top: "-8px",
					fontSize: "10px",
					background: props.labelBg ? props.labelBg : "linear-gradient(rgba(251,248,245,1) 50%, white 50%)",
					...props.labelStyles,
				}}
			>
				{props.required && (
					<Typography variant="caption" style={{ color: palette.primary.main, marginLeft: "2px" }}>
						*
					</Typography>
				)}
			</Typography>
			<DatePickerErrorMessage
				isValidationError={isValidationError}
				startDateInvalid={props.startDateInvalid}
				endDateInvalid={props.endDateInvalid}
				label={props.label}
			/>
			<Box display="flex" justifyContent="space-between" alignItems="center">
				{props.state.option && (
					<>
						<IconButton size="small" onClick={props.clearFilter} style={{ padding: "0 8px" }}>
							<RemoveIcon />
						</IconButton>
						<Divider orientation="vertical" flexItem color="#cddcdc" />
					</>
				)}

				<TextField
					title="date-range-input"
					variant="standard"
					disabled
					color="primary"
					placeholder={dateFormat.toLowerCase()}
					value={fieldValue(props.state.option)}
					label=""
					style={{ width: `${fieldValue(props.state.option).length * 9}px` }}
					classes={{
						root: props.state.option ? classes.textFieldSelected : classes.textField,
					}}
					helperText=""
					InputProps={{
						disableUnderline: true,
					}}
					error={false}
				/>
				<IconButton size="small" onClick={props.handleClick} style={{ padding: "0 4px" }}>
					<KeyboardArrowDownSharpIcon color="primary" />
				</IconButton>
			</Box>
			<Popover
				onClose={props.handleClose}
				PaperProps={{
					style: {
						display: "flex",
						border: "none",
						boxShadow: "0 4px 10px 0 rgba(0, 0, 0, 0.1)",
						borderRadius: "10px",
					},
				}}
				open={!!props.state.anchorEl}
				anchorEl={props.state.anchorEl}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
			>
				{!props.selectMonths && (
					<Box
						display="flex"
						justifyContent="space-between"
						minWidth="520px"
						alignItems="flex-start"
						height="324px"
						padding={"24px"}
					>
						{months.map((d, i) => (
							<Month
								key={`${d.getTime()}.${i}`}
								year={d.getFullYear()}
								month={d.getMonth()}
								startDate={props.state.startDate}
								endDate={props.state.endDate}
								index={i}
								goToNextMonths={props.handleGoToNextMonth}
								goToPreviousMonths={props.handleGoToPreviousMonths}
								handleSelectDay={props.handleSelectDate}
							/>
						))}
					</Box>
				)}
				<Divider orientation="vertical" flexItem />
				<Box className={classes.boxList}>
					<List
						style={{
							display: "flex",
							flexDirection: "column",
							width: "300px",
							justifyContent: "space-between",
							overflow: "hidden",
							borderRadius: "4px",
							boxShadow: "none",
							paddingBottom: "7px",
							marginLeft: "5px",
						}}
					>
						{enabledOptions.map((o, i) => (
							<Fragment key={i}>
								<ListItem
									selected={props.state.option === o}
									style={{
										marginBottom: i === enabledOptions.length - 1 ? "-8px" : "0px",
										height: "100%",
										color: props.state.option === o ? palette.primary.main : palette.text.primary,
										alignItems: props.state.option === o && o === "custom" ? "flex-start" : "center",
									}}
									classes={{
										root: classes.listRoot,
									}}
									onClick={() => props.handleClickOption(o)}
									button
								>
									<Radio
										icon={
											<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
												<circle cx="8" cy="8" r="8" fill="#EBF1F1" />
											</svg>
										}
										classes={{
											root: classes.radioRoot,
										}}
										checkedIcon={
											<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
												<circle cx="8" cy="8" r="8" fill="#EBF1F1" />
												<circle r="4" transform="matrix(-1 0 0 1 8 8)" fill="#367BF5" />
											</svg>
										}
										checked={props.state.option === o}
										color="primary"
										name="radio-button"
									/>
									{o !== "custom" && t(o)}
									{o === "custom" && (
										<div className={props.state.option === o && o === "custom" ? classes.openCustom : ""}>
											{t(o)}

											{props.state.option === o && o === "custom" && (
												<Box
													style={{ position: "absolute", top: 42, left: 16 }}
													display="flex"
													justifyContent="space-between"
													alignItems="center"
												>
													<InputMask
														mask={mask}
														placeholder={dateFormat.toLowerCase()}
														maskPlaceholder={dateFormat.toLowerCase()}
														value={props.state.rawStartValue}
														onChange={props.handleChangeStartDate}
														onBlur={() => props.handleLeaveFocus("rawStartValue")}
													>
														<TextField
															title="date-range-input"
															style={{ width: 122, marginRight: 8 }}
															variant="outlined"
															label={t("range-from")}
															helperText=""
															error={false}
														/>
													</InputMask>

													<InputMask
														mask={mask}
														maskPlaceholder={dateFormat.toLowerCase()}
														placeholder={dateFormat.toLowerCase()}
														value={props.state.rawEndValue}
														onChange={props.handleChangeEndDate}
														onBlur={() => props.handleLeaveFocus("rawEndValue")}
													>
														<TextField
															title="date-range-input"
															variant="outlined"
															label={t("range-to")}
															helperText=""
															error={false}
															style={{ width: 122 }}
														/>
													</InputMask>
												</Box>
											)}
										</div>
									)}
								</ListItem>
							</Fragment>
						))}
					</List>
					<Divider style={{ marginTop: 16, marginBottom: 16 }} />
					<Typography className={classes.clearFilter} onClick={props.clearFilter}>
						{t("clear-filter")}
					</Typography>
					<Divider style={{ marginTop: 16, marginBottom: 16 }} />
					<Typography className={classes.note}>{t("time-period-description")}</Typography>
				</Box>
			</Popover>
		</Paper>
	);
}

export default memo(DashboardDatePicker);

const RemoveIcon = () => (
	<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M8.719 2.006a.875.875 0 1 1 1.237 1.238L7.24 5.96l2.81 2.809a.875.875 0 0 1-1.238 1.237l-2.81-2.809-2.808 2.81a.875.875 0 0 1-1.238-1.238l2.81-2.81-2.717-2.715a.875.875 0 0 1 1.238-1.238l2.716 2.716 2.716-2.716z"
			fill="#367BF5"
		/>
	</svg>
);
